import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { GUIDE_ENTRY_ROUTE } from '@/features/auth/constants';
import { trackEvent } from '@/shared/lib/analytics';
import { guideRecruitmentCampaign } from '../config/guide-recruitment.campaign';
import {
  isCampaignActive,
  resolveCampaignContent,
  resolveCampaignDelayMs,
} from '../config/resolveCampaignContent';
import type { ResolvedCampaignContent } from '../config/types';
import {
  markCampaignDismissed,
  markCampaignShownThisSession,
  markGuideFlowEnteredSession,
} from '../lib/campaignDismissal';
import { resolveCampaignEligibility } from '../lib/campaignEligibility';
import { useLandingPageContext } from '../context/LandingPageContext';

interface UseGuideRecruitmentCampaignResult {
  open: boolean;
  content: ResolvedCampaignContent | null;
  onPrimaryCta: () => void;
  onDismiss: (method: 'maybe_later' | 'close') => void;
}

export function useGuideRecruitmentCampaign(): UseGuideRecruitmentCampaignResult {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, permissions, isInitializing } = useAuth();
  const { selectedCity, authModalOpen } = useLandingPageContext();

  const [open, setOpen] = useState(false);
  const [content, setContent] = useState<ResolvedCampaignContent | null>(null);
  const hasTrackedImpression = useRef(false);
  const timerRef = useRef<number | null>(null);
  const hasScheduledRef = useRef(false);

  const selectedCityRef = useRef(selectedCity);
  const userRef = useRef(user);
  const permissionsRef = useRef(permissions);
  const authModalOpenRef = useRef(authModalOpen);
  const isInitializingRef = useRef(isInitializing);

  const isLandingPage = location.pathname === '/';

  useEffect(() => {
    selectedCityRef.current = selectedCity;
  }, [selectedCity]);

  useEffect(() => {
    userRef.current = user;
    permissionsRef.current = permissions;
    authModalOpenRef.current = authModalOpen;
    isInitializingRef.current = isInitializing;
  }, [user, permissions, authModalOpen, isInitializing]);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!isLandingPage || !isCampaignActive(guideRecruitmentCampaign)) {
      clearTimer();
      hasScheduledRef.current = false;
      setOpen(false);
      setContent(null);
      return;
    }

    if (isInitializing) {
      return;
    }

    const eligibility = resolveCampaignEligibility(guideRecruitmentCampaign, {
      user,
      permissions,
      isInitializing,
      isLandingPage,
      authModalOpen,
      userId: user?.id,
    });

    if (!eligibility.show) {
      clearTimer();
      hasScheduledRef.current = false;
      return;
    }

    if (hasScheduledRef.current) {
      return;
    }

    hasScheduledRef.current = true;
    const delayMs = resolveCampaignDelayMs(guideRecruitmentCampaign);

    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;

      if (location.pathname !== '/') {
        hasScheduledRef.current = false;
        return;
      }

      const latestEligibility = resolveCampaignEligibility(guideRecruitmentCampaign, {
        user: userRef.current,
        permissions: permissionsRef.current,
        isInitializing: isInitializingRef.current,
        isLandingPage: true,
        authModalOpen: authModalOpenRef.current,
        userId: userRef.current?.id,
      });

      if (!latestEligibility.show) {
        hasScheduledRef.current = false;
        return;
      }

      const resolved = resolveCampaignContent(guideRecruitmentCampaign, {
        variant: latestEligibility.variant,
        selectedCity: selectedCityRef.current,
      });

      markCampaignShownThisSession();
      setContent(resolved);
      setOpen(true);
    }, delayMs);

    return () => {
      clearTimer();
      hasScheduledRef.current = false;
    };
  }, [
    authModalOpen,
    clearTimer,
    isInitializing,
    isLandingPage,
    location.pathname,
    permissions,
    user,
  ]);

  useEffect(() => {
    if (!open || !content || hasTrackedImpression.current) return;
    hasTrackedImpression.current = true;
    trackEvent('guide_campaign_impression', {
      campaign_id: content.campaignId,
      variant: content.variant,
      ...(content.selectedCity ? { city: content.selectedCity } : {}),
    });
  }, [open, content]);

  const onPrimaryCta = useCallback(() => {
    if (!content) return;

    trackEvent('guide_campaign_cta_clicked', {
      campaign_id: content.campaignId,
      variant: content.variant,
      ...(content.selectedCity ? { city: content.selectedCity } : {}),
    });

    markGuideFlowEnteredSession();
    setOpen(false);

    if (content.variant === 'continue') {
      navigate('/guide-profile/setup');
      return;
    }

    navigate(GUIDE_ENTRY_ROUTE);
  }, [content, navigate]);

  const onDismiss = useCallback(
    (method: 'maybe_later' | 'close') => {
      if (!content) {
        setOpen(false);
        return;
      }

      trackEvent('guide_campaign_dismissed', {
        campaign_id: content.campaignId,
        dismiss_method: method,
      });

      markCampaignDismissed(content.campaignId, user?.id);
      setOpen(false);
    },
    [content, user?.id],
  );

  return { open, content, onPrimaryCta, onDismiss };
}
